import type { DashboardData } from "@crewledger/shared/schemas";
import type { DailyEntryUiRow } from "@/features/daily-entry/utils";
import type { EquipmentSummary, PayPeriodReview } from "@crewledger/shared/schemas";

export const DEMO_COMPANY_NAME = "Ridgepoint Construction Group";

const EQUIP = {
  excavator: "a1000002-0001-4001-8001-000000000001",
  crane: "a1000002-0001-4001-8001-000000000002",
  forklift: "a1000002-0001-4001-8001-000000000003",
  generator: "a1000002-0001-4001-8001-000000000004",
  compactor: "a1000002-0001-4001-8001-000000000005",
} as const;

const WORKERS = {
  w1: "a1000003-0001-4001-8001-000000000001",
  w2: "a1000003-0001-4001-8001-000000000002",
  w3: "a1000003-0001-4001-8001-000000000003",
  w4: "a1000003-0001-4001-8001-000000000004",
  w5: "a1000003-0001-4001-8001-000000000005",
  w6: "a1000003-0001-4001-8001-000000000006",
  w7: "a1000003-0001-4001-8001-000000000007",
  w8: "a1000003-0001-4001-8001-000000000008",
} as const;

const COMPANY_ID = "a1000000-0001-4001-8001-000000000001";

const SITE = {
  downtown: {
    id: "a1000001-0001-4001-8001-000000000001",
    code: "DT-001",
    name: "Downtown Tower",
  },
  harbor: {
    id: "a1000001-0001-4001-8001-000000000002",
    code: "HF-002",
    name: "Harbor Foundation",
  },
  northRidge: {
    id: "a1000001-0001-4001-8001-000000000003",
    code: "NR-003",
    name: "North Ridge Apartments",
  },
} as const;

export { SITE as DEMO_SITES };

export function createDemoDashboard(): DashboardData {
  return {
    greeting: {
      userName: "Sarah Mitchell",
      companyName: DEMO_COMPANY_NAME,
    },
    currentPayPeriod: {
      id: "b2000001-0001-4001-8001-000000000001",
      label: "Jun 16 – Jun 29",
      startDate: "2026-06-16",
      endDate: "2026-06-29",
      status: "IN_REVIEW",
    },
    asOfDate: "2026-06-23",
    currency: "USD",
    kpis: {
      totalHoursToday: 142.5,
      regularHours: 1840,
      overtimeHours: 186,
      doubleTimeHours: 24,
      estimatedGrossPay: 892450,
      activeWorkers: 28,
      equipmentInUse: 9,
      equipmentCost: 42800,
      pendingApprovals: 12,
      exceptionsNeedingReview: 3,
    },
    siteActivity: [
      {
        siteId: SITE.downtown.id,
        siteCode: SITE.downtown.code,
        siteName: SITE.downtown.name,
        totalHours: 720,
        overtimePercent: 14,
        activeWorkers: 12,
        equipmentInUse: 4,
        status: "Submitted",
      },
      {
        siteId: SITE.harbor.id,
        siteCode: SITE.harbor.code,
        siteName: SITE.harbor.name,
        totalHours: 580,
        overtimePercent: 11,
        activeWorkers: 10,
        equipmentInUse: 3,
        status: "Exception",
      },
      {
        siteId: SITE.northRidge.id,
        siteCode: SITE.northRidge.code,
        siteName: SITE.northRidge.name,
        totalHours: 540,
        overtimePercent: 8,
        activeWorkers: 6,
        equipmentInUse: 2,
        status: "Approved",
      },
    ],
    payPeriodProgress: [
      { key: "create", label: "Create Period", state: "complete" },
      { key: "daily_entry", label: "Daily Entry", state: "complete" },
      { key: "calculation", label: "Calculation", state: "complete" },
      { key: "review", label: "Review", state: "active" },
      { key: "approval", label: "Approval", state: "pending" },
      { key: "locked", label: "Locked", state: "pending" },
      { key: "exported", label: "Exported", state: "pending" },
    ],
    crewStatus: {
      onSite: 28,
      missingEntries: 6,
      offSite: 4,
      unassigned: 2,
    },
    smartExceptions: [
      {
        category: "missing_entries",
        categoryLabel: "Missing entries",
        count: 6,
        severity: "MEDIUM",
        items: [
          {
            id: "ex-1",
            message: "6 assigned workers have no time entry for today",
            targetType: "worker",
          },
        ],
      },
      {
        category: "overtime_spike",
        categoryLabel: "Overtime spike",
        count: 2,
        severity: "HIGH",
        items: [
          {
            id: "ex-2",
            message: "Worker EMP-003 exceeded 12 hours on Jun 20",
            targetType: "time_entry",
          },
        ],
      },
      {
        category: "equipment_missing_hours",
        categoryLabel: "Equipment missing hours",
        count: 1,
        severity: "LOW",
        items: [
          {
            id: "ex-3",
            message: "Crane CR-200 logged without usage hours",
            targetType: "equipment",
          },
        ],
      },
    ],
  };
}

export function createDemoDailyEntryRows(): DailyEntryUiRow[] {
  const workers = [
    { id: WORKERS.w1, employeeId: "EMP-001", name: "James Rodriguez", crew: "Core", hours: 8, job: "JOB-301", flags: {} },
    { id: WORKERS.w2, employeeId: "EMP-002", name: "Maria Chen", crew: "Structural", hours: 10, job: "JOB-302", flags: { isManualOvertime: true } },
    { id: WORKERS.w3, employeeId: "EMP-003", name: "Andre Williams", crew: "MEP", hours: 8, job: "JOB-303", flags: { isNightShift: true } },
    { id: WORKERS.w4, employeeId: "EMP-004", name: "Sofia Garcia", crew: "Foundation", hours: 8, job: "JOB-304", flags: { isHazard: true } },
    { id: WORKERS.w5, employeeId: "EMP-005", name: "Tyler Johnson", crew: "Core", hours: 8, job: "JOB-305", flags: {} },
    { id: WORKERS.w6, employeeId: "EMP-006", name: "Keisha Patel", crew: "Finishing", hours: 8, job: "JOB-306", flags: { isTraining: true } },
    { id: WORKERS.w7, employeeId: "EMP-007", name: "Luis Martinez", crew: "Structural", hours: 10, job: "JOB-307", flags: {} },
    { id: WORKERS.w8, employeeId: "EMP-008", name: "Hannah Brown", crew: "MEP", hours: 8, job: "JOB-308", flags: {} },
  ];

  return workers.map((w) => ({
    clientId: w.id,
    workerId: w.id,
    employeeId: w.employeeId,
    workerName: w.name,
    crew: w.crew,
    hours: w.hours,
    jobCode: w.job,
    costCode: "CC-400",
    notes: "",
    flags: {
      isWeekend: false,
      isHoliday: false,
      isNightShift: Boolean(w.flags.isNightShift),
      isHazard: Boolean(w.flags.isHazard),
      isTraining: Boolean(w.flags.isTraining),
      isManualOvertime: Boolean(w.flags.isManualOvertime),
      isManualDoubleTime: false,
    },
    equipment:
      w.id === WORKERS.w7
        ? [{ equipmentId: EQUIP.excavator, usageHours: 6 }]
        : w.id === WORKERS.w8
          ? [{ equipmentId: EQUIP.crane, usageHours: 8 }]
          : [],
  }));
}

export function createDemoEquipment(): EquipmentSummary[] {
  return [
    {
      id: EQUIP.excavator,
      companyId: COMPANY_ID,
      equipmentId: "EX-100",
      name: "CAT 320 Excavator",
      type: "HEAVY_EQUIPMENT",
      siteId: SITE.downtown.id,
      siteCode: SITE.downtown.code,
      siteName: SITE.downtown.name,
      hourlyCost: 85,
      status: "IN_USE",
      requiredCertification: "EXCAVATOR",
      notes: null,
      periodUsageHours: 124,
      estimatedPeriodCost: 10540,
      createdAt: "2026-01-01T00:00:00Z",
      updatedAt: "2026-06-23T00:00:00Z",
    },
    {
      id: EQUIP.crane,
      companyId: COMPANY_ID,
      equipmentId: "CR-200",
      name: "Liebherr Mobile Crane",
      type: "HEAVY_EQUIPMENT",
      siteId: SITE.harbor.id,
      siteCode: SITE.harbor.code,
      siteName: SITE.harbor.name,
      hourlyCost: 120,
      status: "IN_USE",
      requiredCertification: "CRANE_OPERATOR",
      notes: null,
      periodUsageHours: 96,
      estimatedPeriodCost: 11520,
      createdAt: "2026-01-01T00:00:00Z",
      updatedAt: "2026-06-23T00:00:00Z",
    },
    {
      id: EQUIP.forklift,
      companyId: COMPANY_ID,
      equipmentId: "FL-050",
      name: "Toyota Forklift",
      type: "VEHICLE",
      siteId: SITE.downtown.id,
      siteCode: SITE.downtown.code,
      siteName: SITE.downtown.name,
      hourlyCost: 35,
      status: "AVAILABLE",
      requiredCertification: "FORKLIFT",
      notes: null,
      periodUsageHours: 42,
      estimatedPeriodCost: 1470,
      createdAt: "2026-01-01T00:00:00Z",
      updatedAt: "2026-06-23T00:00:00Z",
    },
    {
      id: EQUIP.generator,
      companyId: COMPANY_ID,
      equipmentId: "GN-010",
      name: "Diesel Generator 50kW",
      type: "RENTAL",
      siteId: SITE.northRidge.id,
      siteCode: SITE.northRidge.code,
      siteName: SITE.northRidge.name,
      hourlyCost: 28,
      status: "IN_USE",
      requiredCertification: null,
      notes: "Rental unit — weekly rate applies",
      periodUsageHours: 80,
      estimatedPeriodCost: 2240,
      createdAt: "2026-01-01T00:00:00Z",
      updatedAt: "2026-06-23T00:00:00Z",
    },
    {
      id: EQUIP.compactor,
      companyId: COMPANY_ID,
      equipmentId: "CP-075",
      name: "Compactor Roller",
      type: "HEAVY_EQUIPMENT",
      siteId: SITE.harbor.id,
      siteCode: SITE.harbor.code,
      siteName: SITE.harbor.name,
      hourlyCost: 65,
      status: "MAINTENANCE",
      requiredCertification: "EXCAVATOR",
      notes: "Scheduled service until Jun 25",
      periodUsageHours: 18,
      estimatedPeriodCost: 1170,
      createdAt: "2026-01-01T00:00:00Z",
      updatedAt: "2026-06-23T00:00:00Z",
    },
  ];
}

export function createDemoPayPeriodReview(): PayPeriodReview {
  return {
    payPeriodId: "b2000001-0001-4001-8001-000000000001",
    label: "Jun 16 – Jun 29",
    startDate: "2026-06-16",
    endDate: "2026-06-29",
    status: "IN_REVIEW",
    totals: {
      totalWorkers: 34,
      regularHours: 1840,
      overtimeHours: 186,
      doubleTimeHours: 24,
      holidayHours: 16,
      premiumPay: 12400,
      equipmentCost: 42800,
      grossPay: 892450,
      openExceptions: 3,
      criticalExceptions: 0,
    },
    approvalProgress: {
      workersApproved: 18,
      workersTotal: 34,
      sitesApproved: 1,
      sitesTotal: 3,
      entriesApproved: 142,
      entriesTotal: 168,
      percentComplete: 85,
    },
    integrity: {
      allEntriesHaveSource: true,
      noRuleConflicts: true,
      exceptionsReviewed: false,
      approvalsPending: 12,
      onchainProofStatus: "NONE",
      merkleRootPreview: null,
      onchainProofId: null,
      payPeriodPublicRef: null,
      transactionSignaturePreview: null,
      publishedAt: null,
    },
    workers: [
      {
        id: WORKERS.w1,
        employeeId: "EMP-001",
        name: "James Rodriguez",
        siteId: SITE.downtown.id,
        siteCode: SITE.downtown.code,
        siteName: SITE.downtown.name,
        crew: "Core",
        regularHours: 64,
        overtimeHours: 8,
        doubleTimeHours: 0,
        holidayHours: 0,
        premiumPay: 420,
        grossPay: 3120,
        equipmentCost: 0,
        entryCount: 9,
        status: "COMPLETE",
        openExceptions: 0,
        canApprove: false,
        canRequestCorrection: true,
      },
      {
        id: WORKERS.w2,
        employeeId: "EMP-002",
        name: "Maria Chen",
        siteId: SITE.downtown.id,
        siteCode: SITE.downtown.code,
        siteName: SITE.downtown.name,
        crew: "Structural",
        regularHours: 56,
        overtimeHours: 16,
        doubleTimeHours: 0,
        holidayHours: 0,
        premiumPay: 680,
        grossPay: 3840,
        equipmentCost: 0,
        entryCount: 9,
        status: "PENDING",
        openExceptions: 0,
        canApprove: true,
        canRequestCorrection: true,
      },
      {
        id: WORKERS.w3,
        employeeId: "EMP-003",
        name: "Andre Williams",
        siteId: SITE.harbor.id,
        siteCode: SITE.harbor.code,
        siteName: SITE.harbor.name,
        crew: "MEP",
        regularHours: 48,
        overtimeHours: 20,
        doubleTimeHours: 4,
        holidayHours: 0,
        premiumPay: 920,
        grossPay: 4280,
        equipmentCost: 510,
        entryCount: 9,
        status: "EXCEPTION",
        openExceptions: 1,
        canApprove: false,
        canRequestCorrection: true,
      },
      {
        id: WORKERS.w4,
        employeeId: "EMP-004",
        name: "Sofia Garcia",
        siteId: SITE.harbor.id,
        siteCode: SITE.harbor.code,
        siteName: SITE.harbor.name,
        crew: "Foundation",
        regularHours: 72,
        overtimeHours: 0,
        doubleTimeHours: 0,
        holidayHours: 8,
        premiumPay: 240,
        grossPay: 3120,
        equipmentCost: 0,
        entryCount: 10,
        status: "COMPLETE",
        openExceptions: 0,
        canApprove: false,
        canRequestCorrection: true,
      },
      {
        id: WORKERS.w5,
        employeeId: "EMP-005",
        name: "Tyler Johnson",
        siteId: SITE.northRidge.id,
        siteCode: SITE.northRidge.code,
        siteName: SITE.northRidge.name,
        crew: "Core",
        regularHours: 64,
        overtimeHours: 0,
        doubleTimeHours: 0,
        holidayHours: 0,
        premiumPay: 0,
        grossPay: 2560,
        equipmentCost: 0,
        entryCount: 8,
        status: "NEEDS_CORRECTION",
        openExceptions: 0,
        canApprove: false,
        canRequestCorrection: true,
      },
    ],
    workersTotal: 34,
    sites: [
      {
        id: SITE.downtown.id,
        name: SITE.downtown.name,
        code: SITE.downtown.code,
        regularHours: 720,
        overtimeHours: 86,
        doubleTimeHours: 8,
        holidayHours: 8,
        premiumPay: 4200,
        equipmentCost: 12400,
        grossPay: 342000,
        workerCount: 12,
        openExceptions: 0,
        status: "APPROVED",
        canApprove: false,
      },
      {
        id: SITE.harbor.id,
        name: SITE.harbor.name,
        code: SITE.harbor.code,
        regularHours: 580,
        overtimeHours: 72,
        doubleTimeHours: 12,
        holidayHours: 4,
        premiumPay: 5100,
        equipmentCost: 18600,
        grossPay: 298000,
        workerCount: 10,
        openExceptions: 2,
        status: "EXCEPTION",
        canApprove: true,
      },
      {
        id: SITE.northRidge.id,
        name: SITE.northRidge.name,
        code: SITE.northRidge.code,
        regularHours: 540,
        overtimeHours: 28,
        doubleTimeHours: 4,
        holidayHours: 4,
        premiumPay: 3100,
        equipmentCost: 11800,
        grossPay: 252450,
        workerCount: 12,
        openExceptions: 1,
        status: "PENDING",
        canApprove: true,
      },
    ],
    crews: [
      {
        name: "Core",
        workerCount: 8,
        regularHours: 480,
        overtimeHours: 32,
        doubleTimeHours: 0,
        holidayHours: 0,
        premiumPay: 2100,
        equipmentCost: 4200,
        grossPay: 198000,
        openExceptions: 0,
      },
      {
        name: "Structural",
        workerCount: 7,
        regularHours: 420,
        overtimeHours: 48,
        doubleTimeHours: 8,
        holidayHours: 0,
        premiumPay: 3200,
        equipmentCost: 8600,
        grossPay: 186400,
        openExceptions: 1,
      },
      {
        name: "MEP",
        workerCount: 6,
        regularHours: 360,
        overtimeHours: 36,
        doubleTimeHours: 4,
        holidayHours: 8,
        premiumPay: 2800,
        equipmentCost: 12400,
        grossPay: 172000,
        openExceptions: 1,
      },
    ],
    equipment: [
      {
        id: EQUIP.excavator,
        name: "CAT 320 Excavator",
        code: "EX-100",
        usageHours: 124,
        workerCount: 6,
        equipmentCost: 10540,
      },
      {
        id: EQUIP.crane,
        name: "Liebherr Mobile Crane",
        code: "CR-200",
        usageHours: 96,
        workerCount: 4,
        equipmentCost: 11520,
      },
    ],
    exceptions: [
      {
        id: "a1000004-0001-4001-8001-000000000001",
        type: "OVERTIME_THRESHOLD",
        severity: "HIGH",
        message: "Worker EMP-003 exceeded daily overtime threshold on Jun 20",
        targetType: "time_entry",
        targetId: "a1000005-0001-4001-8001-000000000001",
        status: "OPEN",
      },
      {
        id: "a1000004-0001-4001-8001-000000000002",
        type: "EQUIPMENT_HOURS_MISSING",
        severity: "MEDIUM",
        message: "Crane CR-200 assigned without usage hours on Jun 22",
        targetType: "equipment",
        targetId: EQUIP.crane,
        status: "OPEN",
      },
    ],
    auditActivity: [
      {
        id: "a1000006-0001-4001-8001-000000000001",
        action: "pay_period.submitted_for_review",
        actorName: "Elena Vasquez",
        createdAt: "2026-06-23T14:30:00Z",
        summary: "Submitted pay period for review",
        reason: null,
      },
    ],
    canEdit: false,
    canApprove: true,
    canLock: false,
    canExport: false,
    canRecalculate: true,
  };
}

export interface DemoReportRow {
  employeeId: string;
  name: string;
  payPeriodLabel: string;
  regularHours: number;
  overtimeHours: number;
  doubleTimeHours: number;
  holidayHours: number;
  premiumPay: number;
  baseRate: number;
  grossPay: number;
  equipmentCost: number;
  notes: string;
}

export function createDemoPayrollReport(): DemoReportRow[] {
  return [
    {
      employeeId: "EMP-001",
      name: "James Rodriguez",
      payPeriodLabel: "Jun 16 – Jun 29",
      regularHours: 64,
      overtimeHours: 8,
      doubleTimeHours: 0,
      holidayHours: 0,
      premiumPay: 420,
      baseRate: 42,
      grossPay: 3120,
      equipmentCost: 0,
      notes: "",
    },
    {
      employeeId: "EMP-002",
      name: "Maria Chen",
      payPeriodLabel: "Jun 16 – Jun 29",
      regularHours: 56,
      overtimeHours: 16,
      doubleTimeHours: 0,
      holidayHours: 0,
      premiumPay: 680,
      baseRate: 48,
      grossPay: 3840,
      equipmentCost: 0,
      notes: "Structural lead",
    },
    {
      employeeId: "EMP-003",
      name: "Andre Williams",
      payPeriodLabel: "Jun 16 – Jun 29",
      regularHours: 48,
      overtimeHours: 20,
      doubleTimeHours: 4,
      holidayHours: 0,
      premiumPay: 920,
      baseRate: 52,
      grossPay: 4280,
      equipmentCost: 510,
      notes: "OT exception flagged",
    },
    {
      employeeId: "EMP-004",
      name: "Sofia Garcia",
      payPeriodLabel: "Jun 16 – Jun 29",
      regularHours: 72,
      overtimeHours: 0,
      doubleTimeHours: 0,
      holidayHours: 8,
      premiumPay: 240,
      baseRate: 40,
      grossPay: 3120,
      equipmentCost: 0,
      notes: "",
    },
    {
      employeeId: "EMP-005",
      name: "Tyler Johnson",
      payPeriodLabel: "Jun 16 – Jun 29",
      regularHours: 64,
      overtimeHours: 0,
      doubleTimeHours: 0,
      holidayHours: 0,
      premiumPay: 0,
      baseRate: 40,
      grossPay: 2560,
      equipmentCost: 0,
      notes: "Correction requested",
    },
  ];
}

export function createInitialDemoState() {
  return {
    dashboard: createDemoDashboard(),
    dailyEntryRows: createDemoDailyEntryRows(),
    equipment: createDemoEquipment(),
    payPeriodReview: createDemoPayPeriodReview(),
    payrollReport: createDemoPayrollReport(),
  };
}

export type DemoState = ReturnType<typeof createInitialDemoState>;
